import { z } from "zod";

export const incidentTypeEnum = z.enum([
  "NOISE",
  "DAMAGE",
  "TRESPASSING",
  "THEFT",
  "ACCIDENT",
  "RULE_VIOLATION",
  "OTHER",
]);
export const incidentStatusEnum = z.enum(["OPEN", "INVESTIGATING", "RESOLVED", "CLOSED"]);

const INCIDENT_TYPE_LABELS: Record<string, { es: string; en: string }> = {
  NOISE: { es: "Ruido", en: "Noise" },
  DAMAGE: { es: "Daño", en: "Damage" },
  TRESPASSING: { es: "Allanamiento", en: "Trespassing" },
  THEFT: { es: "Robo", en: "Theft" },
  ACCIDENT: { es: "Accidente", en: "Accident" },
  RULE_VIOLATION: { es: "Violación de Reglas", en: "Rule Violation" },
  OTHER: { es: "Otro", en: "Other" },
};

export function incidentTypeLabel(value: string, lang: "es" | "en"): string {
  return INCIDENT_TYPE_LABELS[value]?.[lang] ?? value;
}
export const keyTypeEnum = z.enum(["UNIT", "COMMON_AREA", "MAINTENANCE", "MASTER"]);
export const keyStatusEnum = z.enum(["ACTIVE", "LOST", "RETURNED"]);

export const createIncidentSchema = z.object({
  location: z.string().min(1, "Ubicación requerida"),
  incidentType: incidentTypeEnum,
  description: z.string().min(5, "Describa el incidente"),
  unitInvolved: z.string().optional().nullable(),
  photoUrls: z.array(z.string().url()).optional().default([]),
  // Supporting files that are not photos: the emailed complaint, a security
  // company's PDF report. A data: URL is allowed because local development without
  // Cloudinary returns the upload inline, exactly as the permits form already does.
  attachmentUrls: z.array(z.string().min(1)).optional().default([]),
});

// Editing an incident, not just resolving it. This used to accept only status and
// resolution while the form sent the whole record, and validateBody strips unknown
// keys — so corrections to the description or the photos were silently discarded.
export const updateIncidentSchema = z.object({
  location: z.string().min(1).optional(),
  incidentType: incidentTypeEnum.optional(),
  description: z.string().min(5).optional(),
  unitInvolved: z.string().optional().nullable(),
  photoUrls: z.array(z.string().url()).optional(),
  attachmentUrls: z.array(z.string().min(1)).optional(),
  status: incidentStatusEnum.optional(),
  resolution: z.string().optional().nullable(),
});

export const createKeySchema = z.object({
  unitId: z.string().optional().nullable(),
  keyType: keyTypeEnum,
  keyCode: z.string().min(1, "Código requerido"),
  notes: z.string().optional().nullable(),
});

export const assignKeySchema = z.object({
  assignedTo: z.string().min(1, "Asignado a quién requerido"),
});

// Admin correction of a key record — every field optional.
export const updateKeySchema = createKeySchema
  .extend({ status: keyStatusEnum })
  .partial();

export const createCameraAccessSchema = z.object({
  userId: z.string().min(1),
  accessLevel: z.string().min(1, "Nivel de acceso requerido"),
  notes: z.string().optional().nullable(),
});

// Admin correction of a camera-access grant — level/notes only.
export const updateCameraAccessSchema = createCameraAccessSchema.partial().omit({ userId: true });
